import { useEffect } from 'react';
import { Button, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import AppScrollArea from '../../components/common/AppScrollArea';
import { useInitiateQuotaApprovalMutation } from '../../slices/approvalsApiSlice';
import OrderListDateInput, { ORDER_DATE_PICKER_FUTURE_MAX_DATE } from '../order/common/OrderListDateInput';

const QuotaReapplyModal = ({ show, onHide, approval, onSubmitted }) => {
	const detail = approval?.detail || {};
	const [initiateQuotaApproval, { isLoading: loadingApproval }] = useInitiateQuotaApprovalMutation();
	const { register, handleSubmit, reset, watch, setValue, setError, clearErrors, formState: { errors } } = useForm({
		defaultValues: {
			quotaKind: detail.quotaKind || 'permanent',
			applyQuota: detail.applyQuota ?? '',
			repaymentTermDays: detail.repaymentTermDays ?? '',
			effectiveFrom: detail.effectiveFrom || '',
			effectiveTo: detail.effectiveTo || '',
			applyReason: detail.rawReason || '',
		},
	});
	const quotaKind = watch('quotaKind');
	const effectiveFrom = watch('effectiveFrom');
	const effectiveTo = watch('effectiveTo');

	useEffect(() => {
		if (!show) return;
		reset({
			quotaKind: detail.quotaKind || 'permanent',
			applyQuota: detail.applyQuota ?? '',
			repaymentTermDays: detail.repaymentTermDays ?? '',
			effectiveFrom: detail.effectiveFrom || '',
			effectiveTo: detail.effectiveTo || '',
			applyReason: detail.rawReason || '',
		});
	}, [detail.applyQuota, detail.effectiveFrom, detail.effectiveTo, detail.quotaKind, detail.rawReason, detail.repaymentTermDays, reset, show]);

	const submitQuotaApproval = async (data) => {
		if (data.quotaKind === 'temporary') {
			const from = String(data.effectiveFrom || '');
			const to = String(data.effectiveTo || '');
			if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) {
				setError('effectiveFrom', { message: '请填写临时额度生效日期' });
			}
			if (!/^\d{4}-\d{2}-\d{2}$/.test(to)) {
				setError('effectiveTo', { message: '请填写临时额度生效日期' });
			}
			if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return;
			if (to < from) {
				setError('effectiveTo', { message: '生效结束日不能早于开始日' });
				return;
			}
		}
		try {
			const created = await initiateQuotaApproval({
				customer: detail.customer,
				customerName: detail.customerName,
				quotaKind: data.quotaKind,
				applyQuota: data.applyQuota,
				repaymentTermDays: data.quotaKind === 'permanent' ? data.repaymentTermDays : undefined,
				effectiveFrom: data.quotaKind === 'temporary' ? data.effectiveFrom : undefined,
				effectiveTo: data.quotaKind === 'temporary' ? data.effectiveTo : undefined,
				rawReason: data.applyReason,
				type: 'quota',
				resubmittedFrom: approval?._id,
			}).unwrap();
			toast.success('已再次提交，待批准');
			onSubmitted?.(created);
			onHide();
		} catch (err) {
			toast.error(err?.data?.message || err?.error || '提交失败');
		}
	};

	return (
		<Modal show={show} onHide={onHide} centered>
			<Form
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
				}}
			>
				<Modal.Header closeButton>
					<Modal.Title>再次编辑额度申请</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '33.25rem', overflow: 'hidden' }}>
					<AppScrollArea fillViewport maxHeight='100%' autoHide={false}>
					<div className='border rounded-3 p-3 mb-3'>
						<div className='fs-11 text-600 mb-1'>客户</div>
						<div className='fw-semibold'>{detail.customerName || '-'}</div>
					</div>
					<div className='mb-3'>
						<div className='fs-10 text-600 mb-2'>额度类型</div>
						<input type='hidden' {...register('quotaKind')} />
						<div className='d-flex gap-2'>
							<Button type='button' size='sm' className='flex-fill' variant={quotaKind === 'permanent' ? 'primary' : 'outline-secondary'} onClick={() => setValue('quotaKind', 'permanent')}>
								永久额度
							</Button>
							<Button type='button' size='sm' className='flex-fill' variant={quotaKind === 'temporary' ? 'primary' : 'outline-secondary'} onClick={() => setValue('quotaKind', 'temporary')}>
								临时额度
							</Button>
						</div>
						<p className='fs-10 text-600 mt-2 mb-0'>{quotaKind === 'temporary' ? '只在生效日期内计入可用额度，不改永久额度。' : '批准后作为客户永久额度，并按账期还款。'}</p>
					</div>
					<Form.Group className='mb-3' controlId='applyQuota'>
						<Form.Label className='fs-10 text-600 mb-1'>
							申请金额 <span className='text-danger'>*</span>
						</Form.Label>
						<InputGroup>
							<Form.Control
								type='number'
								min='0.01'
								step='0.01'
								placeholder='请填写申请金额'
								autoComplete='off'
								isInvalid={!!errors.applyQuota}
								{...register('applyQuota', {
									valueAsNumber: true,
									validate: (value) => (Number.isFinite(value) && value > 0) || '请填写申请额度',
								})}
							/>
							<InputGroup.Text>元</InputGroup.Text>
						</InputGroup>
						{errors.applyQuota ? <div className='text-danger fs-10 mt-1'>{errors.applyQuota.message}</div> : null}
					</Form.Group>
					{quotaKind === 'temporary' ? (
						<>
							<Form.Group className='mb-3'>
								<Form.Label className='fs-10 text-600 mb-1'>
									生效开始 <span className='text-danger'>*</span>
								</Form.Label>
								<OrderListDateInput
									id='reapplyEffectiveFrom'
									name='effectiveFrom'
									value={effectiveFrom}
									fullWidth
									controlSize={null}
									onChange={(e) => {
										setValue('effectiveFrom', e.target.value);
										clearErrors('effectiveFrom');
									}}
									placeholder='请选择开始日期'
									maxDate={ORDER_DATE_PICKER_FUTURE_MAX_DATE}
								/>
								{errors.effectiveFrom ? <div className='text-danger fs-10 mt-1'>{errors.effectiveFrom.message}</div> : null}
							</Form.Group>
							<Form.Group className='mb-3'>
								<Form.Label className='fs-10 text-600 mb-1'>
									生效结束 <span className='text-danger'>*</span>
								</Form.Label>
								<OrderListDateInput
									id='reapplyEffectiveTo'
									name='effectiveTo'
									value={effectiveTo}
									fullWidth
									controlSize={null}
									onChange={(e) => {
										setValue('effectiveTo', e.target.value);
										clearErrors('effectiveTo');
									}}
									placeholder='请选择结束日期'
									maxDate={ORDER_DATE_PICKER_FUTURE_MAX_DATE}
								/>
								{errors.effectiveTo ? <div className='text-danger fs-10 mt-1'>{errors.effectiveTo.message}</div> : null}
							</Form.Group>
						</>
					) : (
						<Form.Group className='mb-3' controlId='repaymentTermDays'>
							<Form.Label className='fs-10 text-600 mb-1'>
								还款账期 <span className='text-danger'>*</span>
							</Form.Label>
							<InputGroup>
								<Form.Control
									type='number'
									min='1'
									step='1'
									placeholder='请填写还款账期天数'
									autoComplete='off'
									isInvalid={!!errors.repaymentTermDays}
									{...register('repaymentTermDays', {
										valueAsNumber: true,
										validate: (value) => quotaKind !== 'permanent' || (Number.isInteger(value) && value > 0) || '请填写还款账期天数',
									})}
								/>
								<InputGroup.Text>天</InputGroup.Text>
							</InputGroup>
							{errors.repaymentTermDays ? <div className='text-danger fs-10 mt-1'>{errors.repaymentTermDays.message}</div> : null}
						</Form.Group>
					)}
					<Form.Group className='mb-0' controlId='applyReason'>
						<Form.Label className='fs-10 text-600 mb-1'>申请说明</Form.Label>
						<Form.Control as='textarea' rows={3} placeholder='请填写申请理由' autoComplete='off' {...register('applyReason')} />
					</Form.Group>
					</AppScrollArea>
				</Modal.Body>
				<Modal.Footer>
					<Button type='button' variant='secondary' onClick={onHide}>
						取消
					</Button>
					<Button type='button' variant='primary' onClick={handleSubmit(submitQuotaApproval)} disabled={loadingApproval} className='d-inline-flex align-items-center'>
						{loadingApproval ? <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' className='me-2' /> : null}
						提交申请
					</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
};

export default QuotaReapplyModal;
